import { useState, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';

/**
 * ✂️ 文字が見切れている場合のみツールチップを表示するフック
 * * @description
 * 要素の幅からはみ出して「...」になっているテキストを検知し、
 * マウスホバー時に全文をツールチップで表示します。
 * * @example
 * // 1. 基本的な使い方
 * const { tooltipProps, TooltipElement } = useTruncatedTooltip();
 * return (
 * <>
 * <p {...tooltipProps}>{item.text}</p>
 * {TooltipElement}
 * </>
 * )
 * * @example
 * // 2. デザインを変更する場合
 * // ⚠️ classNameを上書きする場合は、必ず 'truncate' を書き直してください！
 * // truncateがないと省略判定ができず、ツールチップが出ません。
 * <p 
 * {...tooltipProps} 
 * className="text-blue-500 text-sm truncate" // ← truncate 必須！
 * >
 * {item.text}
 * </p>
 * * @param baseClass - 適用するCSSクラス（デフォルト: "font-semibold truncate"）
 * @param tooltipId - ツールチップの識別ID（通常は変更不要）
 */

export const useTruncatedTooltip = (
  baseClass: string = "font-semibold truncate", 
  tooltipId: string = "common-truncated-tooltip"
) => {
  const [activeTooltipText, setActiveTooltipText] = useState<string | null>(null);

  // マウスホバー時の判定ロジック
  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const element = e.currentTarget;
    const isTruncated = element.scrollWidth > element.clientWidth;

    if (isTruncated && element.textContent) {
      setActiveTooltipText(element.textContent);
    } else {
      setActiveTooltipText(null);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setActiveTooltipText(null);
  }, []);

  // ターゲット要素に渡すProps
  const tooltipProps = {
    className: baseClass,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    "data-tooltip-id": tooltipId,
  };

  // ここでTooltipコンポーネントを定義
  // 設定（スタイルなど）をここに
  const TooltipElement = (
    <Tooltip 
      id={tooltipId} 
      className="!text-xs !z-[9999] !max-w-[350px] !break-words !whitespace-normal !bg-gray-800 !text-white !opacity-100 !px-3 !py-2 !rounded shadow-md"
      content={activeTooltipText || ""}
    />
  );
  return {
    tooltipProps, // <p>タグにつけるやつ
    TooltipElement,    // そのまま配置するだけのコンポーネント
  };
};